import { useMutation, useQuery } from '@tanstack/react-query';
import { Info, Loader2 } from 'lucide-react';
import { useId, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { dialogSurface, flatPrimary } from '@/components/surface/tones';
import { backupsApi, type SourceInput } from '@/lib/backups-api';
import { getErrorMessage, getFieldErrors } from '@/lib/errors';
import { serversApi } from '@/lib/servers-api';
import type { BackupSource } from '@/types/api';

/** The same rule the API applies, so the form can say so before a round trip. */
const DATABASE_NAME = /^[A-Za-z0-9_][A-Za-z0-9_-]{0,63}$/;

/**
 * Adds a backup source, or corrects where one's data is.
 *
 * ## A source is described, not discovered
 *
 * Which server, which directory, which database. Nothing on the server is looked at when it is saved:
 * whether the directory exists is decided when a backup runs, on the machine, by looking — a
 * directory created a minute after the source is a normal order of events.
 *
 * ## No database password is asked for
 *
 * The database is reached with the server's own administrator access — root for MariaDB and MySQL,
 * the `postgres` account for PostgreSQL — which is what an administrator on that machine already has.
 * A form field for the password would be a secret the platform does not need.
 *
 * ## The server is fixed once there are backups
 *
 * The API refuses to move a source that has copies, because the next backup and restore test would go
 * to a machine that has none of its data. So the picker is locked in that case and says what moves a
 * source instead: restoring a backup onto the new server.
 *
 * The form is unmounted when the dialog closes, so every opening starts clean.
 */
export function SourceDialog({
  open,
  source,
  hasBackups,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  /** The source being edited, or null to add one. */
  source: BackupSource | null;
  /** Whether backups of this source exist, which fixes its server. */
  hasBackups: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-lg ${dialogSurface}`}>
        <DialogHeader>
          <DialogTitle>{source === null ? 'Add a backup source' : `Edit ${source.name}`}</DialogTitle>
          <DialogDescription>A directory, a database, or both, on one of your servers.</DialogDescription>
        </DialogHeader>
        {open && (
          <SourceForm
            key={source?.id ?? 'new'}
            source={source}
            hasBackups={hasBackups}
            onCancel={() => onOpenChange(false)}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SourceForm({
  source,
  hasBackups,
  onCancel,
  onSaved,
}: {
  source: BackupSource | null;
  hasBackups: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const nameId = useId();
  const serverId = useId();
  const directoryId = useId();
  const engineId = useId();
  const databaseId = useId();

  const [values, setValues] = useState<SourceInput>(() => ({
    server_id: source?.server_id ?? '',
    name: source?.name ?? '',
    directory: source?.directory ?? '',
    database_engine: source?.database_engine ?? '',
    database_name: source?.database_name ?? '',
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const servers = useQuery({ queryKey: ['servers', 'all'], queryFn: () => serversApi.all(), staleTime: 30_000 });
  const chosen = servers.data?.find((server) => server.id === values.server_id);
  const serverLocked = source !== null && hasBackups;
  // The server this source points at can be gone from the account; it stays selectable as itself so
  // saving an edit does not silently pick another one.
  const missingServer = source !== null && servers.isSuccess && chosen === undefined && values.server_id !== '';

  const set = <K extends keyof SourceInput>(key: K, value: SourceInput[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      const rest = { ...current };
      delete rest[key];
      return rest;
    });
  };

  const save = useMutation({
    mutationFn: (input: SourceInput) =>
      source === null ? backupsApi.addSource(input) : backupsApi.updateSource(source.id, input),
    onSuccess: () => onSaved(),
    onError: (error) => {
      const fields = getFieldErrors(error);
      setErrors(Object.keys(fields).length > 0 ? fields : { form: getErrorMessage(error) });
    },
  });

  const submit = () => {
    const input: SourceInput = {
      ...values,
      name: values.name.trim(),
      directory: values.directory.trim(),
      database_name: values.database_engine === '' ? '' : values.database_name.trim(),
    };
    const found: Record<string, string> = {};

    if (input.name === '') found.name = 'Give the source a name.';
    if (input.server_id === '') found.server_id = 'Choose the server the data is on.';
    if (input.directory !== '' && !input.directory.startsWith('/')) {
      found.directory = 'The directory must be an absolute path, starting with /.';
    }
    if (input.database_engine !== '' && !DATABASE_NAME.test(input.database_name)) {
      found.database_name = 'Enter the database name: letters, digits, underscores and dashes, up to 64 characters.';
    }
    if (input.directory === '' && input.database_engine === '') {
      found.directory = 'Choose a directory, a database, or both. A source with neither has nothing to back up.';
    }

    setErrors(found);

    if (Object.keys(found).length === 0) {
      save.mutate(input);
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {errors.form !== undefined && (
        <Alert variant="danger" className="border-0">
          {errors.form}
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor={nameId}>Name</Label>
        <Input
          id={nameId}
          value={values.name}
          onChange={(event) => set('name', event.target.value)}
          maxLength={120}
          autoComplete="off"
        />
        <FieldError message={errors.name} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={serverId}>Server</Label>
        <Select
          id={serverId}
          value={values.server_id}
          onChange={(event) => set('server_id', event.target.value)}
          disabled={serverLocked || servers.isLoading}
        >
          <option value="">{servers.isLoading ? 'Loading servers…' : 'Choose a server…'}</option>
          {missingServer && <option value={values.server_id}>A server no longer on your account</option>}
          {(servers.data ?? []).map((server) => (
            <option key={server.id} value={server.id}>
              {server.name} ({server.endpoint}){server.ready ? '' : ', not reachable at its last check'}
            </option>
          ))}
        </Select>
        {servers.isError && <FieldError message={`Your servers could not be loaded: ${getErrorMessage(servers.error)}`} />}
        {servers.isSuccess && servers.data.length === 0 && (
          <p className="text-xs text-muted-foreground">You have no servers yet. Add one under Servers first.</p>
        )}
        {serverLocked && (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-px size-3.5 shrink-0 text-primary" aria-hidden />
            This source already has backups from this server, so it stays there. Restoring a backup onto another server
            moves the source along with its data.
          </p>
        )}
        {chosen !== undefined && !chosen.can_act_as_root && chosen.privilege !== 'unknown' && chosen.privilege_problem !== null && (
          <p className="text-xs text-warning">Backups run as root on the server. {chosen.privilege_problem}</p>
        )}
        <FieldError message={errors.server_id} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={directoryId}>Directory</Label>
        <Input
          id={directoryId}
          value={values.directory}
          onChange={(event) => set('directory', event.target.value)}
          className="font-mono"
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          Everything under it is copied. Leave it empty to back up only a database.
        </p>
        <FieldError message={errors.directory} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={engineId}>Database</Label>
          <Select
            id={engineId}
            value={values.database_engine}
            onChange={(event) => set('database_engine', event.target.value as SourceInput['database_engine'])}
          >
            <option value="">No database</option>
            <option value="mysql">MariaDB / MySQL</option>
            <option value="postgresql">PostgreSQL</option>
          </Select>
          <FieldError message={errors.database_engine} />
        </div>

        {values.database_engine !== '' && (
          <div className="space-y-2">
            <Label htmlFor={databaseId}>Database name</Label>
            <Input
              id={databaseId}
              value={values.database_name}
              onChange={(event) => set('database_name', event.target.value)}
              className="font-mono"
              maxLength={64}
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
            />
            <FieldError message={errors.database_name} />
          </div>
        )}
      </div>

      {values.database_engine !== '' && (
        <p className="text-xs text-muted-foreground">
          Read with the server&rsquo;s own administrator access (
          {values.database_engine === 'mysql' ? 'root, through the local socket or root’s option file' : 'the postgres account'}), so no
          database password is needed.
        </p>
      )}

      <FieldError message={errors.scope} />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending} className={flatPrimary}>
          {save.isPending && <Loader2 className="animate-spin" />}
          {source === null ? 'Add source' : 'Save changes'}
        </Button>
      </DialogFooter>
    </form>
  );
}

function FieldError({ message }: { message: string | undefined }) {
  if (message === undefined || message === '') {
    return null;
  }

  return <p className="text-xs text-danger">{message}</p>;
}
